import { useState } from 'react'
import { Button, Checkbox, Input, message, Space, Tag, Tooltip, Typography, Upload } from 'antd'
import { maskData } from '@/api/dataFactory'
import type { MaskDataResult, MaskRuleType } from '@/types'

const { Text } = Typography

/** 内置脱敏规则选项（与后端 MASK_PATTERNS 对齐） */
const RULE_OPTIONS: { value: MaskRuleType; label: string }[] = [
  { value: 'phone', label: '手机号' },
  { value: 'id_card', label: '身份证' },
  { value: 'email', label: '邮箱' },
  { value: 'address', label: '地址' },
  { value: 'bank_card', label: '银行卡' },
]

/** 各规则命中 Tag 文案 */
const HIT_LABELS: Record<MaskRuleType, string> = {
  phone: '手机号',
  id_card: '身份证',
  email: '邮箱',
  address: '地址',
  bank_card: '银行卡',
}

/** 数据脱敏页签：粘贴或上传文件 → 按内置规则脱敏（手机/身份证/邮箱/地址/银行卡） */
export default function MaskDataTab() {
  const [content, setContent] = useState('')
  const [rules, setRules] = useState<MaskRuleType[]>(RULE_OPTIONS.map((r) => r.value))
  const [result, setResult] = useState<MaskDataResult | null>(null)
  const [loading, setLoading] = useState(false)

  /** 执行脱敏 */
  const handleMask = async () => {
    if (!content.trim()) {
      message.warning('请先粘贴或上传待脱敏文本')
      return
    }
    if (!rules.length) {
      message.warning('请至少选择一条脱敏规则')
      return
    }
    setLoading(true)
    try {
      const res = await maskData({ content, rules })
      setResult(res)
      message.success(`脱敏完成，共命中 ${res.total} 处`)
    } finally {
      setLoading(false)
    }
  }

  /** 上传文件读为文本（前端读取，后端仅收文本，避免 multipart） */
  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      setContent(String(reader.result ?? ''))
      message.success(`已载入文件：${file.name}`)
    }
    reader.readAsText(file, 'utf-8')
    return false // 阻止 antd 默认上传
  }

  /** 复制脱敏结果 */
  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.masked)
    message.success('已复制脱敏结果')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px' }}>
      {/* 规则选择 + 上传 */}
      <Space wrap style={{ flexShrink: 0 }}>
        <Checkbox.Group
          options={RULE_OPTIONS}
          value={rules}
          onChange={(v) => setRules(v as MaskRuleType[])}
        />
        <Upload accept=".txt,.csv,.log,.json,.md" showUploadList={false} beforeUpload={handleFile}>
          <Button>上传文件</Button>
        </Upload>
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading}
          onClick={handleMask}
        >
          脱敏
        </Button>
      </Space>

      {/* 输入区 */}
      <Input.TextArea
        placeholder="粘贴待脱敏文本，或点击上方「上传文件」载入…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={7}
        style={{ flexShrink: 0, fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }}
      />

      {/* 命中统计 */}
      {result && (
        <Space wrap style={{ flexShrink: 0 }}>
          <Text type="secondary">命中：</Text>
          {RULE_OPTIONS.filter((r) => result.hits[r.value]).map((r) => (
            <Tag key={r.value} color="warning">{HIT_LABELS[r.value]} × {result.hits[r.value]}</Tag>
          ))}
          {result.total === 0 && <Text type="secondary">未识别到敏感数据</Text>}
        </Space>
      )}

      {/* 结果区 */}
      {result && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 8, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <Text strong>脱敏结果</Text>
            <div style={{ flex: 1 }} />
            <Tooltip title="复制脱敏结果">
              <Button size="small" onClick={handleCopy}>复制</Button>
            </Tooltip>
          </div>
          <Input.TextArea
            readOnly
            value={result.masked}
            style={{
              flex: 1,
              minHeight: 0,
              fontFamily: "'JetBrains Mono', 'Consolas', monospace",
              fontSize: 12,
              background: 'var(--au-bg-cream)',
              resize: 'none',
            }}
          />
        </div>
      )}
    </div>
  )
}
